import React, { useCallback, useEffect, useMemo, useState, useRef, memo, createElement, Component } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, UploadIcon, LinkIcon, GlobeIcon, BookOpenIcon, SparklesIcon, CheckIcon, FileTextIcon, ClipboardListIcon, SearchIcon, Loader2Icon, ExternalLinkIcon, ChevronRightIcon, ChevronDownIcon, FileIcon, ImageIcon, PresentationIcon, CheckCircleIcon, Maximize2Icon, AlertCircleIcon, RefreshCwIcon, PencilIcon, MicIcon, SquareIcon, PlusIcon, YoutubeIcon, MinusIcon, HashIcon, GraduationCapIcon, ClockIcon, BugIcon } from 'lucide-react';
import { ArchitectLoading } from './ArchitectLoading';
import { BlueprintPlanner } from './BlueprintPlanner';
import type { PlannerData } from './BlueprintPlanner';
import { WebSearchModal, type WebSearchResult } from './WebSearchModal';
type SourceType = 'upload' | 'link' | 'search' | 'questionbank' | 'text' | null;
type WorkspacePhase = 'idle' | 'architect' | 'planner' | 'error';
type PlannerErrorType = 'rate_limit_retry' | 'rate_limit_manual' | 'rate_limit_terminal' | 'server_retry' | 'server_fallback_failed' | 'malformed_retry' | 'truncation_retry' | 'malformed_terminal' | null;
interface AssessmentWorkspaceProps {
  onClose: () => void;
  onGenerate: (blueprint: {
    data: PlannerData;
  }, settings: any) => void;
  initialShowSources?: boolean;
  mode?: 'assessment' | 'journey';
}
// Real audio-reactive waveform using frequency data from AnalyserNode
const LiveWaveform = ({
  analyserRef
}: {
  analyserRef: React.RefObject<AnalyserNode | null>;
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animFrameRef = useRef<number>(0);
  useEffect(() => {
    const canvas = canvasRef.current;
    const analyser = analyserRef.current;
    if (!canvas || !analyser) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    analyser.fftSize = 128;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    const draw = () => {
      animFrameRef.current = requestAnimationFrame(draw);
      analyser.getByteFrequencyData(dataArray);
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, rect.width, rect.height);
      const barCount = 40;
      const gap = 2;
      const totalGap = (barCount - 1) * gap;
      const barWidth = Math.max(1, (rect.width - totalGap) / barCount);
      const centerY = rect.height / 2;
      if (rect.width < 10 || rect.height < 4) return;
      for (let i = 0; i < barCount; i++) {
        const dataIndex = Math.floor(i / barCount * bufferLength);
        const value = dataArray[dataIndex] / 255;
        const center = barCount / 2;
        const distFromCenter = Math.abs(i - center) / center;
        const centerWeight = 1 - distFromCenter * 0.4;
        const barHeight = Math.max(2, value * centerWeight * (rect.height * 0.8));
        const x = i * (barWidth + gap);
        const y = centerY - barHeight / 2;
        const alpha = 0.2 + value * 0.4;
        const radius = Math.max(0, barWidth / 2);
        ctx.fillStyle = `rgba(156, 163, 175, ${alpha})`;
        ctx.beginPath();
        ctx.roundRect(x, y, Math.max(0, barWidth), Math.max(0, barHeight), radius);
        ctx.fill();
      }
    };
    draw();
    return () => cancelAnimationFrame(animFrameRef.current);
  }, [analyserRef]);
  return <canvas ref={canvasRef} className="w-full h-6" style={{
    display: 'block'
  }} data-id="element-158" />;
};
const formatTime = (seconds: number) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};
// Demo words for recording simulation
const demoWords = ['Create', 'a', 'quiz', 'on', 'fractions', 'for', 'grade', '5', 'students', 'covering', 'addition', 'and', 'subtraction', 'of', 'fractions', 'with', 'unlike', 'denominators'];
const getFileIcon = (fileName: string) => {
  const ext = fileName.split('.').pop()?.toLowerCase();
  if (ext === 'pdf') return FileTextIcon;
  if (['doc', 'docx'].includes(ext || '')) return FileIcon;
  if (['ppt', 'pptx'].includes(ext || '')) return PresentationIcon;
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext || '')) return ImageIcon;
  return FileIcon;
};
const getFileTypeLabel = (fileName: string) => {
  const ext = fileName.split('.').pop()?.toUpperCase();
  return ext || 'FILE';
};
const assessmentTemplates = [{
  id: 'quick-check',
  name: 'Quick Check',
  shortName: 'Quick',
  tooltip: 'A short diagnostic to quickly assess understanding of a specific concept. Best for formative checks during or after a lesson.',
  prompt: `Topic: [Insert Topic/Chapter]\n\nTask: Create a 5–8 question MCQ assessment based on the attached context or the topic above.\n\nFocus: High-level recall of core concepts.\n\nDifficulty: Foundational / Uniform.\n\nConstraint: Questions must be concise for a rapid (<10 min) check.`,
  questionCount: 6,
  questionRange: '5–8'
}, {
  id: 'practice-set',
  name: 'Practice Set',
  shortName: 'Practice',
  tooltip: 'A focused set of practice questions designed to build mastery through application and progressive difficulty.',
  prompt: `Topic: [Insert Topic/Chapter]\n\nTask: Create a 10–15 question MCQ practice set based on the attached context or the topic above.\n\nFocus: Mastering atomic units (specific skills).\n\nDifficulty: Gradual increase. Sequence from simple facts to multi-step application.\n\nConstraint: Use high-quality distractors targeting common misconceptions.`,
  questionCount: 12,
  questionRange: '10–15'
}, {
  id: 'unit-assessment',
  name: 'Unit Assessment',
  shortName: 'Unit',
  tooltip: 'A comprehensive evaluation covering an entire chapter or unit. Suitable for graded or summative assessment.',
  prompt: `Topic: [Insert Topic/Chapter]\n\nTask: Create a 16–30 question MCQ assessment based on the attached context or the topic above.\n\nFocus: Full topic hierarchy and synthesis.\n\nDifficulty: Tiered progression. Organize into levels — Fundamentals, Application, and Synthesis.\n\nConstraint: Use high-quality distractors targeting common misconceptions.`,
  questionCount: 20,
  questionRange: '16–30'
}];
const journeyTemplates = [{
  id: 'presentation',
  name: 'Presentation',
  shortName: 'Present',
  tooltip: 'Optimized for classroom presentations with desktop-friendly card dimensions. Perfect for teaching sessions.',
  prompt: `Journey Type: Presentation (Classroom)\nTopic / Chapter: \nCurriculum / Exam: \nGrade / Level: \n\nCreate a learning journey optimized for classroom presentation (5–10 topics). Design each topic card for desktop/projector viewing with clear visuals, concise explanations, and interactive discussion points. Include engaging examples and checkpoints suitable for live teaching. Focus on visual clarity and easy navigation for instructor-led sessions.`
}, {
  id: 'assignment',
  name: 'Assignment',
  shortName: 'Assign',
  tooltip: 'Optimized for homework with mobile-friendly card dimensions. Perfect for independent student learning.',
  prompt: `Journey Type: Assignment (Homework)\nTopic / Chapter: \nCurriculum / Exam: \nGrade / Level: \n\nCreate a learning journey optimized for independent study (5–10 topics). Design each topic card for mobile viewing with self-paced explanations, practice activities, and clear instructions. Include progress checkpoints and reflection prompts. Focus on mobile-friendly layouts that work well for students learning at home on their devices.`
}];
const LEVEL_SEGMENTS = [{
  id: 'school',
  label: 'School'
}, {
  id: 'university',
  label: 'University'
}, {
  id: 'professional',
  label: 'Professional'
}] as const;
const LEVEL_SUBLEVELS: Record<string, string[]> = {
  school: ['Pre-School', 'Kindergarten', 'Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5', 'Year 6', 'Year 7', 'Year 8', 'Year 9', 'Year 10', 'Year 11', 'Year 12', 'Year 13'],
  university: ['Year 1', 'Year 2', 'Year 3', 'Year 4', 'Postgraduate'],
  professional: []
};
const getSourceIconComponent = (type: 'file' | 'link' | 'text' | 'web') => {
  switch (type) {
    case 'file':
      return FileTextIcon;
    case 'link':
      return LinkIcon;
    case 'text':
      return ClipboardListIcon;
    default:
      return GlobeIcon;
  }
};
// Initial mock data for BlueprintPlanner
const initialAssessmentPlannerData: PlannerData = {
  title: 'Photosynthesis Practice',
  summary: ['This assessment focuses on the core ideas of photosynthesis based on the provided topic and source context.', 'It covers what photosynthesis is, why it matters, and the main inputs and outputs involved.', 'It also checks understanding of the role of chlorophyll and sunlight in the process.', 'The assessment is designed to validate foundational conceptual understanding through concise questions.', 'Overall, it serves as a quick check of the most important concepts a learner should retain.'],
  coverage: {
    total_questions: 8,
    topic_distribution: [{
      topic: 'Definition and Purpose',
      weight_percent: 25,
      question_count: 2
    }, {
      topic: 'Inputs and Outputs',
      weight_percent: 25,
      question_count: 2
    }, {
      topic: 'Role of Chlorophyll and Sunlight',
      weight_percent: 25,
      question_count: 2
    }, {
      topic: 'Process Understanding and Misconceptions',
      weight_percent: 25,
      question_count: 2
    }]
  },
  question_plan: [{
    index: 1,
    question_type: 'MCQ',
    taxonomy: 'R',
    core_concept: 'Definition of Photosynthesis',
    intent: 'direct definition'
  }, {
    index: 2,
    question_type: 'MCQ',
    taxonomy: 'R',
    core_concept: 'Purpose of Photosynthesis',
    intent: 'function-based identification'
  }, {
    index: 3,
    question_type: 'MCQ',
    taxonomy: 'U',
    core_concept: 'Raw Materials Required',
    intent: 'explain why each input is needed'
  }, {
    index: 4,
    question_type: 'MCQ',
    taxonomy: 'U',
    core_concept: 'Products of Photosynthesis',
    intent: 'interpret the chemical equation'
  }, {
    index: 5,
    question_type: 'MCQ',
    taxonomy: 'U',
    core_concept: 'Role of Chlorophyll',
    intent: 'explain function in own words'
  }, {
    index: 6,
    question_type: 'MCQ',
    taxonomy: 'A',
    core_concept: 'Role of Sunlight',
    intent: 'predict outcome when variable changes'
  }, {
    index: 7,
    question_type: 'MCQ',
    taxonomy: 'A',
    core_concept: 'Sequence of Photosynthesis Process',
    intent: 'apply steps to a new scenario'
  }, {
    index: 8,
    question_type: 'MCQ',
    taxonomy: 'A',
    core_concept: 'Common Misconceptions in Photosynthesis',
    intent: 'identify error in a given explanation'
  }]
};
const initialPlaybookPlannerData: PlannerData = {
  title: 'Photosynthesis Playbook',
  summary: ['This Playbook introduces learners to the core principles of photosynthesis.', 'It covers what photosynthesis is, why it matters, and the main inputs and outputs involved.', 'Content is structured for Grade 8 students with short, visual learning cards and audio narration.', 'Each topic ends with a recap and practice questions to reinforce understanding.'],
  source_notes: ['The provided source did not include specific examples of C4 plants — generated content uses general examples.', 'No diagrams of the chloroplast structure were available in the source; visuals will be AI-generated.'],
  topic_breakdown: [{
    topic: 'What is Photosynthesis?',
    weight_percent: 38,
    question_count: 5
  }, {
    topic: 'Inputs and Outputs',
    weight_percent: 31,
    question_count: 4
  }, {
    topic: 'Why Photosynthesis Matters',
    weight_percent: 31,
    question_count: 4
  }],
  card_plan: [{
    index: 1,
    type: 'L',
    mode: 'text-image',
    title: 'The Definition of Photosynthesis',
    topic: 'What is Photosynthesis?',
    intent: 'Introduce what photosynthesis is and why all life depends on it'
  }, {
    index: 2,
    type: 'L',
    mode: 'text-image',
    title: 'Where Does It Happen?',
    topic: 'What is Photosynthesis?',
    intent: 'Show the role of chloroplasts and leaves with a labelled visual'
  }, {
    index: 3,
    type: 'L',
    mode: 'text-image',
    title: 'The Raw Ingredients',
    topic: 'What is Photosynthesis?',
    intent: 'Explain sunlight, water, and CO₂ as inputs and how plants get them'
  }, {
    index: 4,
    type: 'A',
    mode: 'interactive',
    title: 'Explore: Inputs and Outputs',
    topic: 'What is Photosynthesis?',
    intent: 'Student adjusts input sliders and observes output changes on a graph'
  }, {
    index: 5,
    type: 'L',
    mode: 'text',
    title: 'A Simple Analogy',
    topic: 'What is Photosynthesis?',
    intent: 'Use a relatable comparison to make the process concrete and memorable'
  }, {
    index: 6,
    type: 'R',
    mode: 'text',
    title: 'Topic Recap',
    topic: 'What is Photosynthesis?',
    intent: 'Summarise definition, inputs, outputs, and key location'
  }, {
    index: 7,
    type: 'P',
    mode: 'text',
    title: 'Practice Questions',
    topic: 'What is Photosynthesis?',
    questionCount: 4
  }, {
    index: 8,
    type: 'L',
    mode: 'text-image',
    title: 'Sunlight as an Energy Source',
    topic: 'Inputs and Outputs',
    intent: 'Explain how light energy drives the reaction'
  }, {
    index: 9,
    type: 'L',
    mode: 'text-image',
    title: 'The Role of Water',
    topic: 'Inputs and Outputs',
    intent: 'Describe how water is absorbed and split during the process'
  }, {
    index: 10,
    type: 'L',
    mode: 'text-image',
    title: 'Carbon Dioxide and Oxygen',
    topic: 'Inputs and Outputs',
    intent: 'Show gas exchange through stomata with a visual'
  }, {
    index: 11,
    type: 'L',
    mode: 'text',
    title: 'Putting It All Together',
    topic: 'Inputs and Outputs',
    intent: 'Combine all inputs and outputs into the full equation'
  }, {
    index: 12,
    type: 'R',
    mode: 'text',
    title: 'Topic Recap',
    topic: 'Inputs and Outputs',
    intent: 'Summarise the three inputs, two outputs, and the equation'
  }, {
    index: 13,
    type: 'P',
    mode: 'text',
    title: 'Practice Questions',
    topic: 'Inputs and Outputs',
    questionCount: 3
  }, {
    index: 14,
    type: 'L',
    mode: 'text-image',
    title: 'Energy for the Food Chain',
    topic: 'Why Photosynthesis Matters',
    intent: 'Show how photosynthesis is the base of all food webs'
  }, {
    index: 15,
    type: 'L',
    mode: 'text-image',
    title: 'Oxygen Production',
    topic: 'Why Photosynthesis Matters',
    intent: 'Explain the link between photosynthesis and breathable air'
  }, {
    index: 16,
    type: 'L',
    mode: 'text',
    title: 'Global Carbon Balance',
    topic: 'Why Photosynthesis Matters',
    intent: 'Describe how plants absorb CO₂ and help regulate climate'
  }, {
    index: 17,
    type: 'A',
    mode: 'interactive',
    title: 'Impact on Human Life',
    topic: 'Why Photosynthesis Matters',
    intent: 'Interactive scenario exploring what happens if photosynthesis stops'
  }, {
    index: 18,
    type: 'R',
    mode: 'text',
    title: 'Topic Recap',
    topic: 'Why Photosynthesis Matters',
    intent: 'Summarise food chains, oxygen, and carbon balance'
  }, {
    index: 19,
    type: 'P',
    mode: 'text',
    title: 'Practice Questions',
    topic: 'Why Photosynthesis Matters',
    questionCount: 4
  }]
};
const MAX_SOURCES = 5;
const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
export function AssessmentWorkspace({
  onClose,
  onGenerate,
  initialShowSources,
  mode = 'assessment'
}: AssessmentWorkspaceProps) {
  const isJourney = mode === 'journey';
  const accent = isJourney ? '#2563EB' : '#00B75F';
  const accentHover = isJourney ? '#1d4ed8' : '#00a055';
  // Phase state
  const [phase, setPhase] = useState<WorkspacePhase>('idle');
  const [plannerErrorType, setPlannerErrorType] = useState<PlannerErrorType>(null);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [maxRetries, setMaxRetries] = useState(0);
  const [architectStatusMessage, setArchitectStatusMessage] = useState('');
  const [devMode, setDevMode] = useState(false);
  // Mobile Tab State
  const [mobileTab, setMobileTab] = useState<'inputs' | 'preview'>('inputs');
  // Mobile Sources collapsed state
  const [isSourcesCollapsedMobile, setIsSourcesCollapsedMobile] = useState(false);
  // Resizing State
  const [leftPanelWidth, setLeftPanelWidth] = useState(380);
  const [sourcesHeight, setSourcesHeight] = useState<number | null>(null);
  const [isResizingHorizontal, setIsResizingHorizontal] = useState(false);
  const [isResizingVertical, setIsResizingVertical] = useState(false);
  const [isInstructionsFocused, setIsInstructionsFocused] = useState(false);
  const [isExpandedEditorOpen, setIsExpandedEditorOpen] = useState(false);
  // Source state
  const [activeSourceTab, setActiveSourceTab] = useState<SourceType>(null);
  const [uploadedFiles, setUploadedFiles] = useState<Array<{
    name: string;
  }>>([]);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkConfirmed, setLinkConfirmed] = useState(false);
  const [copiedText, setCopiedText] = useState('');
  const [copiedTextConfirmed, setCopiedTextConfirmed] = useState(false);
  const [importedWebSources, setImportedWebSources] = useState<WebSearchResult[]>([]);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isTextModalOpen, setIsTextModalOpen] = useState(false);
  const [isSourcesModalOpen, setIsSourcesModalOpen] = useState(initialShowSources ?? false);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  // Instructions state
  const [prompt, setPrompt] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [questionCount, setQuestionCount] = useState<number>(8);
  // Playbook state (for journey mode)
  const [topicCount, setTopicCount] = useState<number>(3);
  const [cardsPerTopic, setCardsPerTopic] = useState<number>(5);
  const [playbookLength, setPlaybookLength] = useState<'standard' | 'in-depth'>('standard');
  const [isLengthDropdownOpen, setIsLengthDropdownOpen] = useState(false);
  const lengthDropdownRef = useRef<HTMLDivElement>(null);
  // Level picker state
  const [levelSegment, setLevelSegment] = useState<'school' | 'university' | 'professional' | null>(null);
  const [levelSubLevel, setLevelSubLevel] = useState<string | null>(null);
  const [levelDisplayLabel, setLevelDisplayLabel] = useState('');
  const [isLevelDropdownOpen, setIsLevelDropdownOpen] = useState(false);
  const levelDropdownRef = useRef<HTMLDivElement>(null);
  // Generating overlay state
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingSeconds, setGeneratingSeconds] = useState(0);
  const generatingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Blueprint state
  const [plannerData, setPlannerData] = useState<PlannerData>(isJourney ? initialPlaybookPlannerData : initialAssessmentPlannerData);
  const [lastGeneratedHash, setLastGeneratedHash] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [validationMessage, setValidationMessage] = useState('');
  const validationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  // Recording State
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [liveTranscript, setLiveTranscript] = useState('');
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const transcriptTimeouts = useRef<ReturnType<typeof setTimeout>[]>([]);
  // Audio Refs
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // Resizing Logic
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (isResizingHorizontal && containerRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const newWidth = e.clientX - containerRect.left;
      if (newWidth >= 280 && newWidth <= 500) {
        setLeftPanelWidth(newWidth);
      }
    } else if (isResizingVertical && containerRef.current) {
      // Sources is at the BOTTOM, so sourcesHeight = distance from mouse to container bottom
      const containerRect = containerRef.current.getBoundingClientRect();
      const newSourcesHeight = containerRect.bottom - e.clientY;
      // Clamp: min 120px for sources, leave at least 200px for instructions
      const maxSourcesHeight = containerRect.height - 200;
      if (newSourcesHeight >= 120 && newSourcesHeight <= maxSourcesHeight) {
        setSourcesHeight(newSourcesHeight);
      }
    }
  }, [isResizingHorizontal, isResizingVertical]);
  const handleMouseUp = useCallback(() => {
    setIsResizingHorizontal(false);
    setIsResizingVertical(false);
  }, []);
  useEffect(() => {
    if (isResizingHorizontal || isResizingVertical) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = isResizingHorizontal ? 'col-resize' : 'row-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizingHorizontal, isResizingVertical, handleMouseMove, handleMouseUp]);
  // Click outside logic for level dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (levelDropdownRef.current && !levelDropdownRef.current.contains(event.target as Node)) {
        setIsLevelDropdownOpen(false);
      }
    };
    if (isLevelDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isLevelDropdownOpen]);
  // Initialize sourcesHeight to 50% of left panel on mount
  useEffect(() => {
    if (sourcesHeight === null && containerRef.current) {
      const containerHeight = containerRef.current.getBoundingClientRect().height;
      // Each panel gets ~50% minus some space for headers and drag handle
      setSourcesHeight(Math.floor(containerHeight / 2));
    }
  }, [sourcesHeight]);
  // Recording Logic
  const cleanupAudio = useCallback(() => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    analyserRef.current = null;
  }, []);
  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true
      });
      mediaStreamRef.current = stream;
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 128;
      analyser.smoothingTimeConstant = 0.7;
      source.connect(analyser);
      analyserRef.current = analyser;
      setIsRecording(true);
      setRecordingSeconds(0);
      setLiveTranscript('');
      timerRef.current = setInterval(() => {
        setRecordingSeconds(prev => prev + 1);
      }, 1000);
      const timeouts: ReturnType<typeof setTimeout>[] = [];
      demoWords.forEach((word, i) => {
        const t = setTimeout(() => {
          setLiveTranscript(prev => prev ? prev + ' ' + word : word);
        }, 600 + i * 280);
        timeouts.push(t);
      });
      transcriptTimeouts.current = timeouts;
    } catch (err) {
      console.error('Microphone access denied:', err);
    }
  }, []);
  const stopRecording = useCallback(() => {
    setIsRecording(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    transcriptTimeouts.current.forEach(clearTimeout);
    transcriptTimeouts.current = [];
    if (liveTranscript.trim()) {
      setPrompt(prev => {
        const separator = prev.trim() ? '\n' : '';
        return prev + separator + liveTranscript;
      });
    }
    setLiveTranscript('');
    setRecordingSeconds(0);
    cleanupAudio();
    setTimeout(() => textareaRef.current?.focus(), 150);
  }, [liveTranscript, cleanupAudio]);
  const cancelRecording = useCallback(() => {
    setIsRecording(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    transcriptTimeouts.current.forEach(clearTimeout);
    transcriptTimeouts.current = [];
    setLiveTranscript('');
    setRecordingSeconds(0);
    cleanupAudio();
  }, [cleanupAudio]);
  // Keyboard shortcuts during recording
  useEffect(() => {
    if (!isRecording) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        cancelRecording();
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        stopRecording();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isRecording, cancelRecording, stopRecording]);
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (validationTimerRef.current) clearTimeout(validationTimerRef.current);
      transcriptTimeouts.current.forEach(clearTimeout);
      cleanupAudio();
    };
  }, [cleanupAudio]);
  const totalSourceCount = uploadedFiles.length + (linkConfirmed ? 1 : 0) + (copiedTextConfirmed ? 1 : 0) + importedWebSources.length;
  const remainingSlots = MAX_SOURCES - totalSourceCount;
  const hasSource = useMemo(() => {
    return !!(uploadedFiles.length > 0 || linkConfirmed || copiedTextConfirmed || importedWebSources.length > 0);
  }, [uploadedFiles, linkConfirmed, copiedTextConfirmed, importedWebSources]);
  const sourceSummaries = useMemo(() => {
    const items: Array<{
      type: 'file' | 'link' | 'text' | 'web';
      label: string;
    }> = [];
    uploadedFiles.forEach(f => items.push({
      type: 'file',
      label: f.name
    }));
    if (linkConfirmed) items.push({
      type: 'link',
      label: linkUrl
    });
    if (copiedTextConfirmed) items.push({
      type: 'text',
      label: copiedText.slice(0, 60) + (copiedText.length > 60 ? '...' : '')
    });
    if (importedWebSources.length > 0) {
      importedWebSources.forEach(ws => {
        items.push({
          type: 'web',
          label: ws.title
        });
      });
    }
    return items;
  }, [uploadedFiles, linkConfirmed, linkUrl, copiedTextConfirmed, copiedText, importedWebSources]);
  const showValidation = useCallback((msg: string) => {
    setValidationMessage(msg);
    if (validationTimerRef.current) clearTimeout(validationTimerRef.current);
    validationTimerRef.current = setTimeout(() => setValidationMessage(''), 4000);
  }, []);
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    if (remainingSlots <= 0) {
      showValidation(`You've reached the ${MAX_SOURCES}-source limit. Remove a source to add more.`);
      e.target.value = '';
      return;
    }
    const oversized = Array.from(files).filter(f => f.size > MAX_FILE_SIZE_BYTES);
    if (oversized.length > 0) {
      showValidation(`"${oversized[0].name}" exceeds the ${MAX_FILE_SIZE_MB}MB limit.`);
    }
    const validFiles = Array.from(files).filter(f => f.size <= MAX_FILE_SIZE_BYTES);
    if (validFiles.length === 0) {
      e.target.value = '';
      return;
    }
    const newFiles = validFiles.map(f => ({
      name: f.name
    }));
    const slotsForFiles = remainingSlots;
    if (newFiles.length > slotsForFiles) {
      showValidation(`Only ${slotsForFiles} slot${slotsForFiles === 1 ? '' : 's'} remaining. Some files were not added.`);
    }
    setUploadedFiles(prev => {
      return [...prev, ...newFiles.slice(0, slotsForFiles)];
    });
    setIsSourcesCollapsedMobile(true);
    setIsSourcesModalOpen(false);
    e.target.value = '';
  };
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const droppedFiles = e.dataTransfer.files;
    if (!droppedFiles || droppedFiles.length === 0) return;
    if (remainingSlots <= 0) {
      showValidation(`You've reached the ${MAX_SOURCES}-source limit. Remove a source to add more.`);
      return;
    }
    const validExtensions = ['.pdf', '.doc', '.docx', '.ppt', '.pptx', '.jpg', '.jpeg', '.png', '.gif', '.webp'];
    const oversized = Array.from(droppedFiles).filter(f => f.size > MAX_FILE_SIZE_BYTES);
    if (oversized.length > 0) {
      showValidation(`"${oversized[0].name}" exceeds the ${MAX_FILE_SIZE_MB}MB limit.`);
    }
    const validFiles = Array.from(droppedFiles).filter(f => {
      const ext = '.' + f.name.split('.').pop()?.toLowerCase();
      return validExtensions.includes(ext) && f.size <= MAX_FILE_SIZE_BYTES;
    }).map(f => ({
      name: f.name
    }));
    if (validFiles.length > 0) {
      if (validFiles.length > remainingSlots) {
        showValidation(`Only ${remainingSlots} slot${remainingSlots === 1 ? '' : 's'} remaining. Some files were not added.`);
      }
      setUploadedFiles(prev => {
        return [...prev, ...validFiles.slice(0, remainingSlots)];
      });
      setIsSourcesCollapsedMobile(true);
    }
  }, [remainingSlots, linkConfirmed, copiedTextConfirmed, importedWebSources.length, showValidation]);
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);
  const handleLinkAdd = () => {
    if (linkUrl.trim() && remainingSlots > 0) {
      setLinkConfirmed(true);
      setIsSourcesCollapsedMobile(true);
    } else if (remainingSlots <= 0) {
      showValidation(`You've reached the ${MAX_SOURCES}-source limit. Remove a source to add more.`);
    }
  };
  const handleTextAdd = () => {
    if (copiedText.trim() && remainingSlots > 0) {
      setCopiedTextConfirmed(true);
      setIsSourcesCollapsedMobile(true);
    } else if (remainingSlots <= 0) {
      showValidation(`You've reached the ${MAX_SOURCES}-source limit. Remove a source to add more.`);
    }
  };
  const handleTemplateSelect = (template: (typeof assessmentTemplates)[0]) => {
    if (selectedTemplate === template.id) {
      if (prompt === template.prompt) setPrompt('');
      setSelectedTemplate(null);
    } else {
      setSelectedTemplate(template.id);
      setPrompt(template.prompt);
      if ('questionCount' in template) {
        setQuestionCount(template.questionCount);
      }
      setIsInstructionsFocused(true);
    }
  };
  const handlePlaybookLengthSelect = (length: 'standard' | 'in-depth') => {
    setPlaybookLength(length);
  };
  const getCurrentInputsHash = useCallback(() => {
    const sourceNames = uploadedFiles.map(f => f.name).join('|');
    const webNames = importedWebSources.map(w => w.title).join('|');
    return `${prompt}::${questionCount}::${topicCount}::${cardsPerTopic}::${playbookLength}::${levelDisplayLabel}::${sourceNames}::${linkConfirmed ? linkUrl : ''}::${copiedTextConfirmed ? copiedText : ''}::${webNames}`;
  }, [prompt, questionCount, topicCount, cardsPerTopic, playbookLength, levelDisplayLabel, uploadedFiles, importedWebSources, linkConfirmed, linkUrl, copiedTextConfirmed, copiedText]);
  const handleBuildPlanner = () => {
    setLastGeneratedHash(getCurrentInputsHash());
    setErrorMessage('');
    setPhase('architect');
    setMobileTab('preview');
  };
  const handleRegeneratePlan = () => {
    setLastGeneratedHash(getCurrentInputsHash());
    setErrorMessage('');
    setPhase('architect');
    setMobileTab('preview');
  };
  const handleArchitectError = useCallback((message: string) => {
    setErrorMessage(message);
    setPhase('error');
    setMobileTab('preview');
  }, []);
  const handleRetry = () => {
    setErrorMessage('');
    setPlannerErrorType(null);
    setArchitectStatusMessage('');
    setPhase('architect');
    setMobileTab('preview');
  };
  const handleBackToEdit = () => {
    setErrorMessage('');
    setPlannerErrorType(null);
    setArchitectStatusMessage('');
    setPhase('idle');
  };
  const handleGenerateAssessment = () => {
    setIsGenerating(true);
    setGeneratingSeconds(0);
    // Start elapsed timer
    generatingTimerRef.current = setInterval(() => {
      setGeneratingSeconds(prev => prev + 1);
    }, 1000);
    // Call onGenerate (backend will respond eventually)
    onGenerate({
      data: plannerData
    }, {});
  };
  const handleCancelGenerate = () => {
    setIsGenerating(false);
    setGeneratingSeconds(0);
    if (generatingTimerRef.current) {
      clearInterval(generatingTimerRef.current);
      generatingTimerRef.current = null;
    }
  };
  // Cleanup generating timer on unmount
  useEffect(() => {
    return () => {
      if (generatingTimerRef.current) clearInterval(generatingTimerRef.current);
    };
  }, []);
  const isInputsDirty = getCurrentInputsHash() !== lastGeneratedHash;
  const canGenerate = isJourney ? (plannerData.topic_breakdown?.length || 0) > 0 : (plannerData.question_plan?.length || 0) > 0;
  const handleCloseClick = () => {
    if (prompt.trim() || hasSource) {
      setShowCloseConfirm(true);
    } else {
      onClose();
    }
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-0 sm:p-4 md:p-6" data-id="element-159">
      <motion.div initial={{
      opacity: 0,
      scale: 0.97,
      y: 12
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.97,
      y: 12
    }} transition={{
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1]
    }} className="bg-white shadow-2xl flex flex-col overflow-hidden w-full h-full sm:rounded-2xl sm:max-w-6xl sm:max-h-[94vh]" data-id="element-160">
        {/* Header / Step Bar */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100 flex-shrink-0 bg-white" data-id="element-161">
          <div className="flex items-center gap-2 sm:gap-4" data-id="element-162">
            <div className={`text-xs sm:text-sm flex items-center gap-1.5 ${phase === 'idle' ? 'font-semibold text-gray-900' : 'text-gray-500'}`} data-id="element-163">
              {(hasSource || prompt.trim().length > 0) && phase !== 'idle' ? <CheckIcon size={14} className="text-[#00B75F]" data-id="element-164" /> : null}
              <span className="hidden sm:inline" data-id="element-165">Sources & Instructions</span>
              <span className="sm:hidden" data-id="element-166">Setup</span>
            </div>
            <ChevronRightIcon size={14} className="text-gray-300" data-id="element-167" />
            <div className={`text-xs sm:text-sm flex items-center gap-1.5 ${phase === 'architect' || phase === 'planner' || phase === 'error' ? 'font-semibold text-gray-900' : 'text-gray-400'}`} data-id="element-168">
              {phase === 'architect' && <Loader2Icon size={14} className="animate-spin text-gray-400" data-id="element-169" />}
              {isGenerating && <CheckIcon size={14} className="text-[#00B75F]" data-id="element-170" />}
              Planner
            </div>
            <ChevronRightIcon size={14} className="text-gray-300" data-id="element-171" />
            <div className={`text-xs sm:text-sm flex items-center gap-1.5 ${isGenerating ? 'font-semibold text-gray-900' : 'text-gray-400'}`} data-id="element-172">
              {isGenerating && <Loader2Icon size={14} className="animate-spin text-gray-400" data-id="element-173" />}
              {isJourney ? 'Playbook' : 'Assessment'}
            </div>
          </div>
          <div className="flex items-center gap-2" data-id="element-174">
            <button onClick={() => setDevMode(!devMode)} className={`p-2 rounded-lg hover:bg-gray-50 transition-colors ${devMode ? 'text-[#00B75F] bg-[#00B75F]/10' : 'text-gray-400 hover:text-gray-600'}`} title="Toggle Dev Mode" data-id="element-175">
              <BugIcon size={20} data-id="element-176" />
            </button>
            <button onClick={handleCloseClick} disabled={isGenerating} className={`p-2 -mr-2 rounded-lg hover:bg-gray-50 text-gray-400 hover:text-gray-600 transition-colors ${isGenerating ? 'opacity-40 pointer-events-none' : ''}`} data-id="element-177">
              <XIcon size={20} data-id="element-178" />
            </button>
          </div>
        </div>

        {/* Mobile Tab Bar */}
        <div className={`md:hidden flex border-b border-gray-200 bg-gray-50 flex-shrink-0 ${isGenerating ? 'opacity-50 pointer-events-none' : ''}`} data-id="element-179">
          <button onClick={() => setMobileTab('inputs')} className={`flex-1 py-3 text-sm font-semibold transition-colors border-b-2 ${mobileTab === 'inputs' ? `border-[${accent}] text-[${accent}] bg-white` : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-180">
            Setup
          </button>
          <button onClick={() => setMobileTab('preview')} className={`flex-1 py-3 text-sm font-semibold transition-colors border-b-2 ${mobileTab === 'preview' ? `border-[${accent}] text-[${accent}] bg-white` : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-181">
            Preview
          </button>
        </div>

        {/* Main Content Area - Split Panel */}
        <div ref={containerRef} className="flex-1 overflow-hidden flex relative bg-gray-50/50" data-id="element-182">
          {/* Left Panel (resizable width, scrollable) */}
          <div style={{
          '--panel-width': `${leftPanelWidth}px`
        } as React.CSSProperties} className={`flex-shrink-0 bg-gray-50 flex flex-col overflow-y-auto md:overflow-hidden w-full md:w-[var(--panel-width)] ${mobileTab === 'inputs' ? 'flex' : 'hidden md:flex'} ${isGenerating ? 'opacity-50 blur-[1px] pointer-events-none select-none' : ''}`} data-id="element-183">
            {/* Instructions Section */}
            <div className="flex-1 flex flex-col min-h-0 md:min-h-0 overflow-hidden" data-id="element-184">
              <div className="w-full flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200 flex-shrink-0" data-id="element-185">
                <span className="text-sm font-semibold text-gray-900" data-id="element-186">
                  Instructions
                </span>
                <button onClick={() => setIsExpandedEditorOpen(true)} className="p-1 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors" aria-label="Expand editor" data-id="element-187">
                  <Maximize2Icon size={14} data-id="element-188" />
                </button>
              </div>

              <div className="flex-1 flex flex-col min-h-0 overflow-y-auto px-3 py-2" data-id="element-189">
                {/* Question Count & Level Row */}
                <div className="flex items-center gap-2 mb-2 flex-shrink-0" data-id="element-190">
                  {/* Question Count Stepper (Assessment only) */}
                  {!isJourney && <div className="flex items-center justify-between bg-white border border-gray-200 rounded-md px-2 py-1 w-auto min-w-[140px]" data-id="element-191">
                      <div className="flex items-center gap-1.5" data-id="element-192">
                        <HashIcon size={12} className="text-gray-400" data-id="element-193" />
                        <span className="text-[11px] font-semibold text-gray-700" data-id="element-194">
                          Questions
                        </span>
                      </div>
                      <div className="flex items-center bg-gray-50 border border-gray-200 rounded overflow-hidden ml-2" data-id="element-195">
                        <button onClick={() => setQuestionCount(Math.max(1, questionCount - 1))} disabled={questionCount <= 1} className="p-0.5 px-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-id="element-196">
                          <MinusIcon size={10} data-id="element-197" />
                        </button>
                        <div className={`w-6 text-center text-[11px] font-bold ${questionCount >= 32 ? 'text-red-500' : questionCount >= 28 ? 'text-amber-600' : 'text-gray-900'}`} data-id="element-198">
                          {questionCount}
                        </div>
                        <button onClick={() => setQuestionCount(Math.min(32, questionCount + 1))} disabled={questionCount >= 32} className="p-0.5 px-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-id="element-199">
                          <PlusIcon size={10} data-id="element-200" />
                        </button>
                      </div>
                    </div>}

                  {/* Level Picker */}
                  <div className="relative" ref={levelDropdownRef} data-id="element-201">
                    <button onClick={() => setIsLevelDropdownOpen(!isLevelDropdownOpen)} title={isJourney ? 'Who is this for? Helps Ainstein set the right vocabulary, tone, and complexity for your content.' : undefined} className={`flex items-center justify-between rounded-md px-2 py-1 transition-colors border min-w-[130px] ${levelDisplayLabel ? `bg-[${accent}]/5 border-[${accent}]/20 hover:border-[${accent}]/40` : 'bg-white border-gray-200 hover:border-gray-300'}`} data-id="element-202">
                      <div className="flex items-center gap-1.5 flex-shrink-0" data-id="element-203">
                        <GraduationCapIcon size={12} className={levelDisplayLabel ? `text-[${accent}]` : 'text-gray-400'} data-id="element-204" />
                        <span className={`text-[11px] font-semibold ${levelDisplayLabel ? `text-[${accent}]` : 'text-gray-700'}`} data-id="element-205">
                          Audience
                        </span>
                      </div>
                      <div className="flex items-center gap-1 ml-2" data-id="element-206">
                        <span className={`text-[11px] font-medium truncate max-w-[70px] ${levelDisplayLabel ? `text-[${accent}]` : 'text-gray-400'}`} data-id="element-207">
                          {levelDisplayLabel || 'Select...'}
                        </span>
                        <ChevronDownIcon size={10} className={`${levelDisplayLabel ? `text-[${accent}]/60` : 'text-gray-400'} transition-transform duration-200 flex-shrink-0 ${isLevelDropdownOpen ? 'rotate-180' : ''}`} data-id="element-208" />
                      </div>
                    </button>

                    {/* Portal-rendered dropdown to escape overflow clipping */}
                    {isLevelDropdownOpen && createPortal(<div className="fixed inset-0 z-[200]" onClick={e => {
                    if (e.target === e.currentTarget) {
                      setIsLevelDropdownOpen(false);
                      setLevelSegment(null);
                    }
                  }} data-id="element-209">
                          <motion.div initial={{
                      opacity: 0,
                      y: -4
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} transition={{
                      duration: 0.12
                    }} className="absolute bg-white border border-gray-200 rounded-xl shadow-lg z-[201] w-[168px]" style={{
                      top: levelDropdownRef.current ? levelDropdownRef.current.getBoundingClientRect().bottom + 4 : 0,
                      left: levelDropdownRef.current ? levelDropdownRef.current.getBoundingClientRect().left : 0
                    }} data-id="element-210">
                            <div className="flex flex-col p-1.5" data-id="element-211">
                              {LEVEL_SEGMENTS.map((segment, segIdx) => {
                          const hasSublevels = segment.id !== 'professional' && LEVEL_SUBLEVELS[segment.id]?.length > 0;
                          const isHovered = levelSegment === segment.id;
                          return <div key={segment.id} className="relative" data-segment-id={segment.id} onMouseEnter={e => {
                            if (window.innerWidth >= 768) {
                              setLevelSegment(segment.id as any);
                            }
                          }} onMouseLeave={e => {
                            // Only clear if not moving to the sublevel panel
                            const related = e.relatedTarget as HTMLElement;
                            if (related?.closest?.('[data-sublevel-panel]')) return;
                            if (window.innerWidth >= 768) {
                              setLevelSegment(null);
                            }
                          }} onClick={() => {
                            if (window.innerWidth < 768) {
                              setLevelSegment(levelSegment === segment.id ? null : segment.id as any);
                            }
                            if (segment.id === 'professional') {
                              setLevelDisplayLabel('Professional');
                              setLevelSubLevel(null);
                              setIsLevelDropdownOpen(false);
                              setLevelSegment(null);
                            }
                          }} data-id="element-212">
                                    <button className={`w-full text-left px-3 py-2 rounded-lg text-xs font-medium transition-colors flex items-center justify-between ${isHovered ? 'bg-gray-50 text-gray-900' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-213">
                                      <span data-id="element-214">{segment.label}</span>
                                      {hasSublevels && <ChevronRightIcon size={12} className={`${isHovered ? 'text-gray-600' : 'text-gray-400'}`} data-id="element-215" />}
                                    </button>

                                    {/* Mobile: Expandable Sub-levels */}
                                    {hasSublevels && isHovered && <div className="md:hidden mt-1 ml-2 border-l-2 border-gray-100 pl-2 pb-1 space-y-0.5" data-id="element-216">
                                        {LEVEL_SUBLEVELS[segment.id].map(subLevel => <button key={subLevel} onClick={e => {
                                e.stopPropagation();
                                setLevelSubLevel(subLevel);
                                const segmentLabel = LEVEL_SEGMENTS.find(s => s.id === segment.id)?.label;
                                setLevelDisplayLabel(`${segmentLabel} · ${subLevel}`);
                                setIsLevelDropdownOpen(false);
                                setLevelSegment(null);
                              }} className={`w-full text-left px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${levelSubLevel === subLevel ? `bg-[${accent}]/10 text-[${accent}]` : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-217">
                                              {subLevel}
                                            </button>)}
                                      </div>}
                                  </div>;
                        })}
                            </div>
                          </motion.div>

                          {/* Desktop: Sub-level flyout panel */}
                          {levelSegment && levelSegment !== 'professional' && LEVEL_SUBLEVELS[levelSegment]?.length > 0 && <div data-sublevel-panel className="hidden md:block absolute bg-white border border-gray-200 rounded-xl shadow-lg p-1.5 w-[220px] max-h-[320px] overflow-y-auto z-[202]" style={(() => {
                      if (!levelDropdownRef.current) return {};
                      const triggerRect = levelDropdownRef.current.getBoundingClientRect();
                      const segmentEl = document.querySelector(`[data-segment-id="${levelSegment}"]`);
                      const segmentRect = segmentEl?.getBoundingClientRect();
                      return {
                        left: triggerRect.left + 168 + 4,
                        top: segmentRect ? segmentRect.top : triggerRect.bottom + 4
                      };
                    })()} onMouseEnter={() => {
                      // Keep segment active while hovering sublevel panel
                    }} onMouseLeave={() => {
                      setLevelSegment(null);
                    }} data-id="element-218">
                                <div className="grid grid-cols-2 gap-1" data-id="element-219">
                                  {LEVEL_SUBLEVELS[levelSegment].map(subLevel => <button key={subLevel} onClick={() => {
                          setLevelSubLevel(subLevel);
                          const segmentLabel = LEVEL_SEGMENTS.find(s => s.id === levelSegment)?.label;
                          setLevelDisplayLabel(`${segmentLabel} · ${subLevel}`);
                          setIsLevelDropdownOpen(false);
                          setLevelSegment(null);
                        }} className={`text-left px-2.5 py-2 rounded-lg text-xs font-medium transition-colors ${levelSubLevel === subLevel ? `bg-[${accent}] text-white` : `text-gray-600 hover:bg-[${accent}]/10 hover:text-[${accent}]`}`} data-id="element-220">
                                        {subLevel}
                                      </button>)}
                                </div>
                              </div>}
                        </div>, document.body)}
                  </div>

                  {/* Length Picker (Journey only) */}
                  {isJourney && <div className="relative" ref={lengthDropdownRef} data-id="element-221">
                      <button onClick={() => setIsLengthDropdownOpen(!isLengthDropdownOpen)} title={`How long should this playbook take?\n\nStandard — A focused overview. 2–4 topics, 4–5 cards each. ~15–20 min.\nIn-Depth — A comprehensive deep-dive. 5–7 topics, 5–8 cards each. ~35–45 min.`} className={`flex items-center justify-between rounded-md px-2 py-1 transition-colors border min-w-[140px] ${playbookLength ? `bg-[${accent}]/5 border-[${accent}]/20 hover:border-[${accent}]/40` : 'bg-white border-gray-200 hover:border-gray-300'}`} data-id="element-222">
                        <div className="flex items-center gap-1.5 flex-shrink-0" data-id="element-223">
                          <ClockIcon size={12} className={playbookLength ? `text-[${accent}]` : 'text-gray-400'} data-id="element-224" />
                          <span className={`text-[11px] font-semibold ${playbookLength ? `text-[${accent}]` : 'text-gray-700'}`} data-id="element-225">
                            Length
                          </span>
                        </div>
                        <div className="flex items-center gap-1 ml-2" data-id="element-226">
                          <span className={`text-[11px] font-medium truncate max-w-[80px] ${playbookLength ? `text-[${accent}]` : 'text-gray-400'}`} data-id="element-227">
                            {playbookLength === 'standard' ? 'Standard' : playbookLength === 'in-depth' ? 'In-Depth' : 'Select...'}
                          </span>
                          <ChevronDownIcon size={10} className={`${playbookLength ? `text-[${accent}]/60` : 'text-gray-400'} transition-transform duration-200 flex-shrink-0 ${isLengthDropdownOpen ? 'rotate-180' : ''}`} data-id="element-228" />
                        </div>
                      </button>

                      {/* Portal-rendered dropdown */}
                      {isLengthDropdownOpen && createPortal(<div className="fixed inset-0 z-[200]" onClick={e => {
                    if (e.target === e.currentTarget) {
                      setIsLengthDropdownOpen(false);
                    }
                  }} data-id="element-229">
                            <motion.div initial={{
                      opacity: 0,
                      y: -4
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} transition={{
                      duration: 0.12
                    }} className="absolute bg-white border border-gray-200 rounded-xl shadow-lg z-[201] w-[168px]" style={{
                      top: lengthDropdownRef.current ? lengthDropdownRef.current.getBoundingClientRect().bottom + 4 : 0,
                      left: lengthDropdownRef.current ? lengthDropdownRef.current.getBoundingClientRect().left : 0
                    }} data-id="element-230">
                              <div className="flex flex-col p-1.5" data-id="element-231">
                                {[{
                          value: 'standard',
                          label: 'Standard',
                          desc: '2–4 topics · ~15–20 min',
                          tooltip: 'A focused overview covering key concepts. Best for quick lessons or refreshers.'
                        }, {
                          value: 'in-depth',
                          label: 'In-Depth',
                          desc: '5–7 topics · ~35–45 min',
                          tooltip: 'A comprehensive deep-dive with detailed explanations. Best for full lessons or chapters.'
                        }].map(option => <button key={option.value} title={option.tooltip} onClick={() => {
                          handlePlaybookLengthSelect(option.value as 'standard' | 'in-depth');
                          setIsLengthDropdownOpen(false);
                        }} className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${playbookLength === option.value ? `bg-[${accent}] text-white` : `text-gray-700 hover:bg-[${accent}]/10 hover:text-[${accent}]`}`} data-id="element-232">
                                    <span className="text-xs font-medium block" data-id="element-233">
                                      {option.label}
                                    </span>
                                    <span className={`text-[10px] block mt-0.5 ${playbookLength === option.value ? 'text-white/70' : 'text-gray-400'}`} data-id="element-234">
                                      {option.desc}
                                    </span>
                                  </button>)}
                              </div>
                            </motion.div>
                          </div>, document.body)}
                    </div>}
                </div>

                {/* Templates or Playbook Controls */}
                {!isJourney && <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide mb-2 flex-shrink-0" data-id="element-235">
                    {assessmentTemplates.map(template => {
                  const isSelected = selectedTemplate === template.id;
                  return <button key={template.id} onClick={() => handleTemplateSelect(template as any)} className={`flex-shrink-0 px-2 py-1 rounded-md text-[10px] font-medium transition-all ${isSelected ? 'bg-[#00B75F]/10 text-[#00B75F]' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`} data-id="element-236">
                          {template.name}
                        </button>;
                })}
                  </div>}

                {/* Textarea */}
                <div className={`flex-1 rounded-lg border bg-white overflow-hidden transition-all flex flex-col min-h-[120px] ${isInstructionsFocused ? `border-[${accent}] shadow-sm ring-2 ring-[${accent}]/10` : 'border-gray-200 focus-within:border-gray-400 focus-within:shadow-sm'} ${questionCount >= 28 && !isJourney ? 'border-orange-300 focus-within:border-orange-400 focus-within:ring-orange-400/10' : ''}`} data-id="element-237">
                  <textarea ref={textareaRef} value={prompt} onChange={e => setPrompt(e.target.value)} onFocus={() => setIsInstructionsFocused(true)} onBlur={() => setIsInstructionsFocused(false)} placeholder={isJourney ? 'Describe what learners should understand: topic, key concepts, learning goals…' : 'Describe your assessment: topic, grade level, question types, difficulty…'} className="flex-1 w-full p-3 bg-transparent text-gray-900 placeholder-gray-400 focus:outline-none resize-none text-sm leading-relaxed" data-id="element-238" />
                </div>
              </div>
            </div>

            {/* Vertical Drag Handle */}
            <div className="hidden md:block h-2 bg-gray-200 hover:bg-gray-300 cursor-row-resize flex-shrink-0 transition-colors group" onMouseDown={() => setIsResizingVertical(true)} data-id="element-239">
              <div className="flex items-center justify-center gap-1 h-full" data-id="element-240">
                <div className="w-1 h-1 rounded-full bg-gray-300 group-hover:bg-gray-400 transition-colors" data-id="element-241" />
                <div className="w-1 h-1 rounded-full bg-gray-300 group-hover:bg-gray-400 transition-colors" data-id="element-242" />
                <div className="w-1 h-1 rounded-full bg-gray-300 group-hover:bg-gray-400 transition-colors" data-id="element-243" />
              </div>
            </div>

            {/* Sources Section */}
            <div style={sourcesHeight !== null ? {
            '--sources-height': `${sourcesHeight}px`
          } as React.CSSProperties : undefined} className={`flex flex-col flex-shrink-0 h-auto ${sourcesHeight !== null ? 'md:h-[var(--sources-height)]' : 'md:flex-1'} md:overflow-hidden ${!isResizingVertical ? 'md:transition-[height] md:duration-200 md:ease-in-out' : ''}`} data-id="element-244">
              <button onClick={() => setIsSourcesCollapsedMobile(prev => !prev)} className="md:pointer-events-none w-full flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200 flex-shrink-0" data-id="element-245">
                <div className="flex items-center gap-2" data-id="element-246">
                  <span className="text-sm font-semibold text-gray-900" data-id="element-247">
                    Sources
                  </span>
                  <span className="text-[10px] font-semibold text-gray-600 bg-gray-200 px-2 py-0.5 rounded-full" data-id="element-248">
                    {totalSourceCount}/{MAX_SOURCES}
                  </span>
                  <ChevronDownIcon size={16} className={`md:hidden text-gray-400 transition-transform duration-200 ${isSourcesCollapsedMobile ? '-rotate-90' : ''}`} data-id="element-249" />
                </div>
                <div className="flex items-center gap-2 ml-auto" data-id="element-250">
                  {!hasSource && <p className="text-[11px] text-gray-400 hidden md:block italic" data-id="element-251">
                      Add sources to reduce guesswork.
                    </p>}
                </div>
              </button>

              <div className={`flex-1 overflow-y-auto px-3 py-2 ${isSourcesCollapsedMobile ? 'hidden md:block' : ''}`} data-id="element-252">
                <input type="file" ref={fileInputRef} className="hidden" accept=".pdf,.doc,.docx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.webp" onChange={handleFileUpload} multiple data-id="element-253" />

                {!hasSource ? <div className="flex flex-col items-center justify-center py-3 gap-2" onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave} data-id="element-254">
                    {isDragOver ? <div className="w-full border-2 border-dashed border-gray-400 bg-gray-50 rounded-xl p-4 text-center" data-id="element-255">
                        <UploadIcon size={20} className="text-gray-400 mx-auto mb-1" data-id="element-256" />
                        <p className="text-sm font-medium text-gray-600" data-id="element-257">
                          Drop files here
                        </p>
                      </div> : <>
                        <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center" data-id="element-258">
                          <UploadIcon size={16} className="text-gray-400" data-id="element-259" />
                        </div>
                        <p className="text-[11px] text-gray-400 text-center" data-id="element-260">
                          Upload files, paste links, or search the web
                        </p>
                        <button onClick={() => setIsSourcesModalOpen(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-xs font-semibold text-gray-700 transition-all" data-id="element-261">
                          <PlusIcon size={12} data-id="element-262" />
                          Add Sources
                        </button>
                      </>}
                  </div> : <div className="flex flex-col gap-3" data-id="element-263">
                    <div className="divide-y divide-gray-100 border border-gray-200 rounded-xl overflow-hidden bg-white" data-id="element-264">
                      {uploadedFiles.map((file, idx) => {
                    const FileIcon_ = getFileIcon(file.name);
                    return <div key={`file-${idx}`} className="flex items-center gap-2.5 px-3 py-2 group" data-id="element-265">
                            <FileIcon_ size={14} className="text-gray-400 flex-shrink-0" data-id="element-266" />
                            <div className="flex-1 min-w-0" data-id="element-267">
                              <span className="text-xs font-medium text-gray-700 truncate block" data-id="element-268">
                                {file.name}
                              </span>
                            </div>
                            <button onClick={() => setUploadedFiles(prev => prev.filter((_, i) => i !== idx))} className="p-1 rounded-md hover:bg-red-50 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all" data-id="element-269">
                              <XIcon size={12} data-id="element-270" />
                            </button>
                          </div>;
                  })}
                      {linkConfirmed && <div className="flex items-center gap-2.5 px-3 py-2 group" data-id="element-271">
                          <LinkIcon size={14} className="text-gray-400 flex-shrink-0" data-id="element-272" />
                          <div className="flex-1 min-w-0" data-id="element-273">
                            <a href={linkUrl} target="_blank" rel="noopener noreferrer" className="text-xs font-medium text-gray-700 hover:text-blue-600 truncate block cursor-pointer transition-colors" data-id="element-274">
                              {linkUrl}
                            </a>
                          </div>
                          <button onClick={() => {
                      setLinkConfirmed(false);
                      setLinkUrl('');
                    }} className="p-1 rounded-md hover:bg-red-50 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all" data-id="element-275">
                            <XIcon size={12} data-id="element-276" />
                          </button>
                        </div>}
                      {copiedTextConfirmed && <div className="flex items-center gap-2.5 px-3 py-2 group" data-id="element-277">
                          <ClipboardListIcon size={14} className="text-gray-400 flex-shrink-0" data-id="element-278" />
                          <div className="flex-1 min-w-0" data-id="element-279">
                            <button onClick={() => setIsTextModalOpen(true)} className="text-xs font-medium text-gray-700 hover:text-blue-600 truncate text-left w-full cursor-pointer transition-colors" data-id="element-280">
                              {copiedText.length > 40 ? copiedText.slice(0, 40) + '...' : 'Copied text'}
                            </button>
                          </div>
                          <button onClick={() => {
                      setCopiedTextConfirmed(false);
                      setCopiedText('');
                    }} className="p-1 rounded-md hover:bg-red-50 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all" data-id="element-281">
                            <XIcon size={12} data-id="element-282" />
                          </button>
                        </div>}
                      {importedWebSources.map(ws => <div key={ws.id} className="flex items-center gap-2.5 px-3 py-2 group" data-id="element-283">
                          <GlobeIcon size={14} className="text-gray-400 flex-shrink-0" data-id="element-284" />
                          <div className="flex-1 min-w-0" data-id="element-285">
                            <a href={ws.url} target="_blank" rel="noopener noreferrer" className="text-xs font-medium text-gray-700 hover:text-blue-600 truncate block cursor-pointer transition-colors" data-id="element-286">
                              {ws.title}
                            </a>
                          </div>
                          <button onClick={() => setImportedWebSources(prev => prev.filter(s => s.id !== ws.id))} className="p-1 rounded-md hover:bg-red-50 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all" data-id="element-287">
                            <XIcon size={12} data-id="element-288" />
                          </button>
                        </div>)}
                    </div>

                    {remainingSlots > 0 && <button onClick={() => setIsSourcesModalOpen(true)} className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-dashed border-gray-300 hover:border-gray-400 bg-white hover:bg-gray-50 text-xs font-medium text-gray-500 hover:text-gray-700 transition-all" data-id="element-289">
                        <PlusIcon size={14} data-id="element-290" />
                        Add More Sources
                      </button>}
                  </div>}
              </div>
            </div>
          </div>

          {/* Horizontal Drag Handle */}
          <div className="hidden md:block w-2 bg-gray-200 hover:bg-gray-300 cursor-col-resize flex-shrink-0 z-10 transition-colors group" onMouseDown={() => setIsResizingHorizontal(true)} data-id="element-291">
            <div className="flex flex-col items-center justify-center gap-1 h-full" data-id="element-292">
              <div className="w-1 h-1 rounded-full bg-gray-300 group-hover:bg-gray-400 transition-colors" data-id="element-293" />
              <div className="w-1 h-1 rounded-full bg-gray-300 group-hover:bg-gray-400 transition-colors" data-id="element-294" />
              <div className="w-1 h-1 rounded-full bg-gray-300 group-hover:bg-gray-400 transition-colors" data-id="element-295" />
            </div>
          </div>

          {/* Right Panel (Content area) */}
          <div className={`flex-1 flex flex-col relative min-w-0 bg-white ${mobileTab === 'preview' ? 'flex' : 'hidden md:flex'}`} data-id="element-296">
            {/* Mobile floating Edit Instructions icon */}
            {mobileTab === 'preview' && (phase === 'planner' || phase === 'error') && <button onClick={() => setIsExpandedEditorOpen(true)} className="md:hidden fixed bottom-20 right-4 z-30 w-10 h-10 bg-white/90 backdrop-blur-sm text-gray-500 rounded-xl shadow-md border border-gray-200 flex items-center justify-center hover:bg-white hover:text-gray-700 active:scale-95 transition-all" aria-label="Edit Instructions" data-id="element-297">
                  <PencilIcon size={16} data-id="element-298" />
                </button>}
            <AnimatePresence mode="wait" data-id="element-299">
              {phase === 'idle' && <motion.div key="idle" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.3
            }} className="flex-1 flex flex-col items-center justify-center text-center p-8" data-id="element-300">
                  <div className="w-20 h-20 bg-gray-50 rounded-2xl flex items-center justify-center mb-5 border border-gray-100" data-id="element-301">
                    <SparklesIcon size={40} className="text-gray-300" data-id="element-302" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-303">
                    Your planner will appear here
                  </h3>
                  <p className="text-sm text-gray-400 font-medium max-w-sm" data-id="element-304">
                    Add sources and instructions in the left panel, then click
                    Build Planner to get started.
                  </p>
                </motion.div>}

              {phase === 'architect' && <motion.div key="architect" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.3
            }} className="flex-1 w-full h-full" data-id="element-305">
                  <ArchitectLoading isOpen={true} onComplete={() => {
                setPhase('planner');
              }} onError={handleArchitectError} hasSource={hasSource} sourceCount={totalSourceCount} data-id="element-306" />
                </motion.div>}

              {phase === 'error' && <motion.div key="error" initial={{
              opacity: 0,
              scale: 0.98
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.3
            }} className="flex-1 flex flex-col items-center justify-center text-center p-8" data-id="element-307">
                  {plannerErrorType === 'rate_limit_manual' ? <>
                      <div className="w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center mb-5 border border-amber-100" data-id="element-308">
                        <AlertCircleIcon size={32} className="text-amber-400" data-id="element-309" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-310">
                        High demand right now
                      </h3>
                      <p className="text-sm text-gray-500 max-w-sm mb-6" data-id="element-311">
                        We're experiencing high demand right now. Tap below to
                        try again when you're ready.
                      </p>
                      <div className="flex items-center gap-3" data-id="element-312">
                        <button onClick={handleRetry} className={`px-5 py-2.5 bg-[${accent}] text-white text-sm font-semibold rounded-xl hover:bg-[${accentHover}] transition-colors flex items-center justify-center gap-2`} data-id="element-313">
                          <RefreshCwIcon size={14} data-id="element-314" />
                          Try Again
                        </button>
                      </div>
                    </> : plannerErrorType === 'rate_limit_terminal' ? <>
                      <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mb-5 border border-red-100" data-id="element-315">
                        <AlertCircleIcon size={32} className="text-red-400" data-id="element-316" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-317">
                        Unable to generate right now
                      </h3>
                      <p className="text-sm text-gray-500 max-w-sm mb-6" data-id="element-318">
                        We're unable to generate your plan right now. Our team
                        has been notified — please try again in a few minutes.
                      </p>
                      <div className="flex items-center gap-3" data-id="element-319">
                        <button onClick={handleBackToEdit} className="px-5 py-2.5 border border-gray-200 text-gray-700 text-sm font-semibold rounded-xl hover:bg-gray-50 transition-colors" data-id="element-320">
                          Try Again Later
                        </button>
                      </div>
                    </> : plannerErrorType === 'server_fallback_failed' || plannerErrorType === 'malformed_terminal' ? <>
                      <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mb-5 border border-red-100" data-id="element-321">
                        <AlertCircleIcon size={32} className="text-red-400" data-id="element-322" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-323">
                        Couldn't generate your plan
                      </h3>
                      <p className="text-sm text-gray-500 max-w-sm mb-6" data-id="element-324">
                        We couldn't generate your plan. Please check your prompt
                        and try again.
                      </p>
                      <div className="flex items-center gap-3" data-id="element-325">
                        <button onClick={handleBackToEdit} className={`px-5 py-2.5 bg-[${accent}] text-white text-sm font-semibold rounded-xl hover:bg-[${accentHover}] transition-colors flex items-center justify-center gap-2`} data-id="element-326">
                          Try Again
                        </button>
                      </div>
                    </> : <>
                      <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mb-5 border border-red-100" data-id="element-327">
                        <AlertCircleIcon size={32} className="text-red-400" data-id="element-328" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-329">
                        Something went wrong
                      </h3>
                      <p className="text-sm text-gray-500 max-w-sm mb-6" data-id="element-330">
                        {errorMessage || "We couldn't generate your planner. This can happen due to a network issue or a temporary problem on our end."}
                      </p>
                      <div className="flex items-center gap-3" data-id="element-331">
                        <button onClick={handleBackToEdit} className="px-5 py-2.5 border border-gray-200 text-gray-700 text-sm font-semibold rounded-xl hover:bg-gray-50 transition-colors" data-id="element-332">
                          Back to Editing
                        </button>
                        <button onClick={handleRetry} className={`px-5 py-2.5 bg-[${accent}] text-white text-sm font-semibold rounded-xl hover:bg-[${accentHover}] transition-colors flex items-center justify-center gap-2`} data-id="element-333">
                          <RefreshCwIcon size={14} data-id="element-334" />
                          Retry
                        </button>
                      </div>
                    </>}
                </motion.div>}

              {phase === 'planner' && <motion.div key="planner" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.3
            }} className="flex-1 w-full h-full flex overflow-hidden" data-id="element-335">
                  <BlueprintPlanner data={plannerData} onDataChange={setPlannerData} data-id="element-336" />
                </motion.div>}
            </AnimatePresence>

            {/* Dev Mode Panel */}
            <AnimatePresence data-id="element-337">
              {devMode && <motion.div initial={{
              opacity: 0,
              y: 20,
              scale: 0.95
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} exit={{
              opacity: 0,
              y: 20,
              scale: 0.95
            }} className="absolute bottom-4 right-4 z-50 bg-white border border-gray-200 shadow-xl rounded-xl p-4 w-80" data-id="element-338">
                  <div className="flex items-center justify-between mb-3" data-id="element-339">
                    <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2" data-id="element-340">
                      <BugIcon size={14} className="text-gray-400" data-id="element-341" />
                      🛠 Dev: Error States
                    </h4>
                    <button onClick={() => setDevMode(false)} className="text-gray-400 hover:text-gray-600" data-id="element-342">
                      <XIcon size={14} data-id="element-343" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 gap-2" data-id="element-344">
                    <button onClick={() => {
                  setPhase('architect');
                  setPlannerErrorType('rate_limit_retry');
                  setArchitectStatusMessage('Generating your Playbook plan… this may take a moment.');
                }} className="text-left px-3 py-1.5 text-xs font-medium bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors" data-id="element-345">
                      Rate Limit → Auto Retry
                    </button>
                    <button onClick={() => {
                  setPhase('error');
                  setPlannerErrorType('rate_limit_manual');
                }} className="text-left px-3 py-1.5 text-xs font-medium bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors" data-id="element-346">
                      Rate Limit → Manual Retry
                    </button>
                    <button onClick={() => {
                  setPhase('error');
                  setPlannerErrorType('rate_limit_terminal');
                }} className="text-left px-3 py-1.5 text-xs font-medium bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors" data-id="element-347">
                      Rate Limit → Terminal
                    </button>
                    <button onClick={() => {
                  setPhase('architect');
                  setPlannerErrorType('server_retry');
                  setArchitectStatusMessage('Still working on your plan — retrying now. (attempt 2 of 3)');
                }} className="text-left px-3 py-1.5 text-xs font-medium bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors" data-id="element-348">
                      500 → Retrying (2/3)
                    </button>
                    <button onClick={() => {
                  setPhase('error');
                  setPlannerErrorType('server_fallback_failed');
                }} className="text-left px-3 py-1.5 text-xs font-medium bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors" data-id="element-349">
                      500 → Terminal
                    </button>
                    <button onClick={() => {
                  setPhase('architect');
                  setPlannerErrorType('malformed_retry');
                  setArchitectStatusMessage('The plan came back incomplete. Retrying with a fresh attempt…');
                }} className="text-left px-3 py-1.5 text-xs font-medium bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors" data-id="element-350">
                      Malformed → Retrying
                    </button>
                    <button onClick={() => {
                  setPhase('architect');
                  setPlannerErrorType('truncation_retry');
                  setArchitectStatusMessage('The plan was too large to process at once. Adjusting and retrying…');
                }} className="text-left px-3 py-1.5 text-xs font-medium bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors" data-id="element-351">
                      Truncated → Retrying
                    </button>
                    <button onClick={() => {
                  setPhase('error');
                  setPlannerErrorType('malformed_terminal');
                }} className="text-left px-3 py-1.5 text-xs font-medium bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors" data-id="element-352">
                      Malformed → Terminal
                    </button>
                    <button onClick={() => {
                  setPhase('idle');
                  setPlannerErrorType(null);
                  setArchitectStatusMessage('');
                }} className="text-left px-3 py-1.5 text-xs font-medium bg-gray-900 text-white hover:bg-gray-800 rounded-lg transition-colors mt-1" data-id="element-353">
                      Reset to Idle
                    </button>
                  </div>
                </motion.div>}
            </AnimatePresence>

            {/* Generating Overlay — sits on top of the planner */}
            <AnimatePresence data-id="element-354">
              {isGenerating && <motion.div key="generating-overlay" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.3
            }} className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm" data-id="element-355">
                  <motion.div animate={{
                y: [0, -6, 0],
                scale: [1, 1.04, 1]
              }} transition={{
                duration: 2.4,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="mb-6" data-id="element-356">
                    <div className={`w-14 h-14 rounded-2xl bg-[${accent}]/10 border border-[${accent}]/20 flex items-center justify-center`} data-id="element-357">
                      <motion.div animate={{
                    rotate: [0, 360]
                  }} transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: 'linear'
                  }} data-id="element-358">
                        <SparklesIcon size={24} className={`text-[${accent}]`} data-id="element-359" />
                      </motion.div>
                    </div>
                  </motion.div>

                  <motion.h3 initial={{
                opacity: 0,
                y: 8
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.4,
                delay: 0.1
              }} className="text-lg font-semibold text-gray-900 mb-2" data-id="element-360">
                    {isJourney ? 'Ainstein is preparing your playbook...' : 'Ainstein is preparing your assessment...'}
                  </motion.h3>

                  <motion.p initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} transition={{
                duration: 0.5,
                delay: 0.6
              }} className="text-sm text-gray-400 font-medium mb-1" data-id="element-361">
                    Good things take a moment.
                  </motion.p>

                  <motion.p initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} transition={{
                duration: 0.3,
                delay: 0.8
              }} className="text-xs text-gray-300 font-mono tabular-nums mb-6" data-id="element-362">
                    {Math.floor(generatingSeconds / 60)}:
                    {(generatingSeconds % 60).toString().padStart(2, '0')}
                  </motion.p>

                  <motion.button initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} transition={{
                duration: 0.3,
                delay: 1.0
              }} onClick={handleCancelGenerate} className="px-5 py-2 border border-gray-200 text-gray-500 text-sm font-medium rounded-xl hover:bg-white hover:text-gray-700 hover:border-gray-300 transition-colors" data-id="element-363">
                    Cancel
                  </motion.button>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className={`px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-100 bg-white flex-shrink-0 ${isGenerating ? 'opacity-50 blur-[1px] pointer-events-none select-none' : ''}`} data-id="element-364">
          <AnimatePresence data-id="element-365">
            {validationMessage && <motion.div initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: 8
          }} transition={{
            duration: 0.2
          }} className="mb-3 flex items-center gap-2 px-4 py-2.5 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 font-medium" data-id="element-366">
                <AlertCircleIcon size={14} className="text-red-400 flex-shrink-0" data-id="element-367" />
                {validationMessage}
                <button onClick={() => setValidationMessage('')} className="ml-auto p-0.5 text-red-400 hover:text-red-600 transition-colors" data-id="element-368">
                  <XIcon size={12} data-id="element-369" />
                </button>
              </motion.div>}
          </AnimatePresence>

          {phase === 'idle' && <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0" data-id="element-370">
              <p className="text-xs text-gray-400 text-center sm:text-left" data-id="element-371">
                Planner generation takes ~1 min
              </p>
              <button onClick={handleBuildPlanner} disabled={!prompt.trim()} className={`w-full sm:w-auto px-6 py-2.5 bg-[${accent}] text-white text-sm font-semibold rounded-xl hover:bg-[${accentHover}] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2`} data-id="element-372">
                <SparklesIcon size={16} data-id="element-373" /> Build Planner
              </button>
            </div>}

          {phase === 'architect' && <div className="flex justify-between items-center sm:justify-end" data-id="element-374">
              <button onClick={handleBackToEdit} className="text-sm text-gray-500 hover:text-gray-700 bg-transparent px-2 py-2.5 font-medium transition-colors" data-id="element-375">
                Cancel
              </button>
              <button disabled className="w-auto px-6 py-2.5 bg-gray-100 text-gray-400 text-sm font-semibold rounded-xl cursor-not-allowed flex items-center justify-center gap-2" data-id="element-376">
                <Loader2Icon size={16} className="animate-spin" data-id="element-377" />{' '}
                {architectStatusMessage || 'Building...'}
              </button>
            </div>}

          {phase === 'error' && <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 sm:gap-0" data-id="element-378">
              <p className={`text-sm font-medium text-center sm:text-left ${plannerErrorType === 'rate_limit_manual' ? 'text-amber-500' : 'text-red-500'}`} data-id="element-379">
                {plannerErrorType === 'rate_limit_manual' ? "We're experiencing high demand right now." : plannerErrorType === 'rate_limit_terminal' ? "We're unable to generate your plan right now." : plannerErrorType === 'server_fallback_failed' || plannerErrorType === 'malformed_terminal' ? "We couldn't generate your plan." : 'Generation failed — adjust your inputs or try again.'}
              </p>
              {plannerErrorType === 'rate_limit_terminal' ? <button onClick={handleBackToEdit} className="w-full sm:w-auto px-6 py-2.5 border border-gray-200 text-gray-700 text-sm font-semibold rounded-xl hover:bg-gray-50 transition-colors" data-id="element-380">
                  Try Again Later
                </button> : plannerErrorType === 'server_fallback_failed' || plannerErrorType === 'malformed_terminal' ? <button onClick={handleBackToEdit} className={`w-full sm:w-auto px-6 py-2.5 bg-[${accent}] text-white text-sm font-semibold rounded-xl hover:bg-[${accentHover}] transition-colors flex items-center justify-center gap-2`} data-id="element-381">
                  Try Again
                </button> : <button onClick={handleRetry} className={`w-full sm:w-auto px-6 py-2.5 bg-[${accent}] text-white text-sm font-semibold rounded-xl hover:bg-[${accentHover}] transition-colors flex items-center justify-center gap-2`} data-id="element-382">
                  <RefreshCwIcon size={14} data-id="element-383" />
                  {plannerErrorType === 'rate_limit_manual' ? 'Try Again' : 'Retry'}
                </button>}
            </div>}

          {phase === 'planner' && <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 sm:gap-0" data-id="element-384">
              <p className="text-sm text-gray-500 text-center sm:text-left" data-id="element-385">
                {isJourney ? <>
                    <span className="font-semibold text-gray-900" data-id="element-386">
                      {plannerData.topic_breakdown?.length || topicCount}
                    </span>{' '}
                    topics <span className="text-gray-300 mx-1" data-id="element-387">·</span>{' '}
                    <span className="font-semibold text-gray-900" data-id="element-388">
                      {plannerData.topic_breakdown?.reduce((acc, t) => acc + t.question_count, 0) || topicCount * cardsPerTopic}
                    </span>{' '}
                    learning cards <span className="text-gray-300 mx-1" data-id="element-389">·</span>{' '}
                    ~{playbookLength === 'standard' ? '15–20' : '35–45'} min to
                    complete
                  </> : <>
                    <span className="font-semibold text-gray-900" data-id="element-390">
                      {plannerData.question_plan?.length || 0}
                    </span>{' '}
                    questions planned (requested {questionCount})
                  </>}
                <span className="text-gray-300 mx-1" data-id="element-391">·</span>
                <span className="text-[11px] text-gray-400" data-id="element-392">
                  ~{isJourney ? '2-4' : '2'} min to generate
                </span>
              </p>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3" data-id="element-393">
                <button onClick={handleGenerateAssessment} disabled={!canGenerate || isGenerating} className={`w-full sm:w-auto px-6 py-2.5 text-sm font-semibold rounded-xl flex items-center justify-center gap-2 transition-colors ${isGenerating ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : isInputsDirty ? canGenerate ? 'border border-gray-200 text-gray-700 hover:bg-gray-50' : 'border border-gray-100 text-gray-400 bg-gray-50 cursor-not-allowed' : canGenerate ? `bg-[${accent}] text-white hover:bg-[${accentHover}]` : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-394">
                  {isGenerating && <Loader2Icon size={16} className="animate-spin" data-id="element-395" />}
                  {isJourney ? 'Generate Playbook' : 'Generate Assessment'}
                </button>
                <div className="relative group" data-id="element-396">
                  <button onClick={handleRegeneratePlan} disabled={!isInputsDirty || isGenerating} className={`w-full sm:w-auto px-5 py-2.5 text-sm font-semibold rounded-xl flex items-center justify-center gap-2 transition-colors ${isGenerating ? 'border border-gray-100 text-gray-400 bg-gray-50 cursor-not-allowed' : isInputsDirty ? `bg-[${accent}] text-white hover:bg-[${accentHover}]` : 'border border-gray-100 text-gray-400 bg-gray-50 cursor-not-allowed'}`} data-id="element-397">
                    <SparklesIcon size={16} className={isInputsDirty && !isGenerating ? 'text-white' : 'text-gray-300'} data-id="element-398" />{' '}
                    {isJourney ? 'Regenerate Plan' : 'Regenerate with New Inputs'}
                  </button>
                  {isInputsDirty && <div className="hidden sm:block absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 text-center" data-id="element-399">
                      Re-run the architect with your updated sources and
                      instructions.
                      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" data-id="element-400"></div>
                    </div>}
                  {!isInputsDirty && <div className="hidden sm:block absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 text-center" data-id="element-401">
                      Edit your sources or instructions to regenerate.
                      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" data-id="element-402"></div>
                    </div>}
                </div>
              </div>
            </div>}
        </div>
      </motion.div>

      {/* Modals */}
      <WebSearchModal isOpen={isSearchModalOpen} onClose={() => setIsSearchModalOpen(false)} initialQuery={searchQuery} onImport={sources => {
      if (remainingSlots <= 0) {
        showValidation(`You've reached the ${MAX_SOURCES}-source limit. Remove a source to add more.`);
        setIsSearchModalOpen(false);
        return;
      }
      if (sources.length > remainingSlots) {
        showValidation(`Only ${remainingSlots} slot${remainingSlots === 1 ? '' : 's'} remaining. Some sources were not added.`);
      }
      setImportedWebSources(prev => [...prev, ...sources.slice(0, remainingSlots)]);
      setIsSearchModalOpen(false);
    }} importedSources={importedWebSources} data-id="element-403" />

      {/* Close Confirmation Overlay */}
      <AnimatePresence data-id="element-404">
        {showCloseConfirm && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 z-[60] flex items-center justify-center bg-white/60 backdrop-blur-sm rounded-2xl" data-id="element-405">
            <motion.div initial={{
          scale: 0.95,
          opacity: 0,
          y: 10
        }} animate={{
          scale: 1,
          opacity: 1,
          y: 0
        }} exit={{
          scale: 0.95,
          opacity: 0,
          y: 10
        }} className="bg-white border border-gray-200 shadow-xl rounded-xl p-6 max-w-sm w-full mx-4" data-id="element-406">
              <div className="flex flex-col items-center text-center mb-6" data-id="element-407">
                <div className="w-12 h-12 bg-red-50 text-red-500 rounded-full flex items-center justify-center mb-4" data-id="element-408">
                  <AlertCircleIcon size={24} data-id="element-409" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-410">
                  Discard unsaved work?
                </h3>
                <p className="text-sm text-gray-500" data-id="element-411">
                  You have added sources or instructions. If you close now, your
                  progress will be lost.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3" data-id="element-412">
                <button onClick={() => setShowCloseConfirm(false)} className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-50 transition-colors" data-id="element-413">
                  Keep Editing
                </button>
                <button onClick={onClose} className="flex-1 px-4 py-2.5 bg-red-500 text-white text-sm font-semibold rounded-lg hover:bg-red-600 transition-colors" data-id="element-414">
                  Discard & Close
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Expanded Editor Modal */}
      <AnimatePresence data-id="element-415">
        {isExpandedEditorOpen && <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4" data-id="element-416">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setIsExpandedEditorOpen(false)} data-id="element-417" />
            <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: 40
        }} transition={{
          duration: 0.25,
          ease: [0.4, 0, 0.2, 1]
        }} className="relative w-full h-full sm:h-auto sm:max-w-5xl sm:max-h-[85vh] bg-white sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden" data-id="element-418">
              {/* Header */}
              <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100 flex-shrink-0" data-id="element-419">
                <div className="flex items-center gap-2.5 sm:gap-3" data-id="element-420">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-[#00B75F]/10 to-[#00B75F]/5 flex items-center justify-center" data-id="element-421">
                    <SparklesIcon size={18} className="text-[#00B75F]" data-id="element-422" />
                  </div>
                  <div data-id="element-423">
                    <h2 className="text-base sm:text-lg font-bold text-gray-900" data-id="element-424">
                      Edit Instructions
                    </h2>
                    <p className="text-[11px] sm:text-xs text-gray-500 hidden sm:block" data-id="element-425">
                      Distraction-free space for detailed instructions
                    </p>
                  </div>
                </div>
                <button onClick={() => setIsExpandedEditorOpen(false)} className="p-2 rounded-lg hover:bg-gray-50 text-gray-400 hover:text-gray-600 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center" data-id="element-426">
                  <XIcon size={20} data-id="element-427" />
                </button>
              </div>

              {/* Templates */}
              <div className="px-4 sm:px-6 py-2.5 sm:py-3 border-b border-gray-100 flex items-center gap-2 overflow-x-auto scrollbar-hide flex-shrink-0" data-id="element-428">
                {assessmentTemplates.map(template => {
              const isSelected = selectedTemplate === template.id;
              return <button key={template.id} onClick={() => handleTemplateSelect(template as any)} disabled={isRecording} className={`flex-shrink-0 px-2 py-1.5 rounded-lg text-xs font-medium transition-all border min-h-[36px] ${isSelected ? 'bg-[#00B75F]/10 border-[#00B75F] text-[#00B75F]' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'} ${isRecording ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-429">
                      {template.name}
                    </button>;
            })}
              </div>

              {/* Expanded Editor Question Count Stepper */}
              {!isJourney ? <div className="px-4 sm:px-6 py-3 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between flex-shrink-0" data-id="element-430">
                  <div className="flex items-center gap-2" data-id="element-431">
                    <HashIcon size={16} className="text-gray-500" data-id="element-432" />
                    <span className="text-sm font-semibold text-gray-900" data-id="element-433">
                      Number of Questions
                    </span>
                    {questionCount >= 28 && <span className="ml-2 text-[11px] font-medium text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full border border-orange-200" data-id="element-434">
                        Approaching max (32)
                      </span>}
                  </div>
                  <div className="flex items-center bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm" data-id="element-435">
                    <button onClick={() => setQuestionCount(Math.max(1, questionCount - 1))} disabled={questionCount <= 1 || isRecording} className="p-2 text-gray-500 hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-436">
                      <MinusIcon size={14} data-id="element-437" />
                    </button>
                    <div className="w-12 text-center text-sm font-semibold text-gray-900 border-x border-gray-100 py-1.5" data-id="element-438">
                      {questionCount}
                    </div>
                    <button onClick={() => setQuestionCount(Math.min(32, questionCount + 1))} disabled={questionCount >= 32 || isRecording} className="p-2 text-gray-500 hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-439">
                      <PlusIcon size={14} data-id="element-440" />
                    </button>
                  </div>
                </div> : <div className="px-4 sm:px-6 py-3 border-b border-gray-100 bg-gray-50/50 flex items-center gap-4 flex-shrink-0" data-id="element-441">
                  {/* Level Picker */}
                  <div className="flex-1 max-w-[200px]" ref={levelDropdownRef} data-id="element-442">
                    <button onClick={() => setIsLevelDropdownOpen(!isLevelDropdownOpen)} className={`w-full flex items-center justify-between rounded-lg px-3 py-2 transition-colors border bg-white border-gray-200 hover:border-gray-300`} data-id="element-443">
                      <div className="flex items-center gap-2" data-id="element-444">
                        <GraduationCapIcon size={14} className="text-gray-500" data-id="element-445" />
                        <span className="text-sm font-semibold text-gray-700" data-id="element-446">
                          Audience
                        </span>
                      </div>
                      <div className="flex items-center gap-1" data-id="element-447">
                        <span className="text-sm font-medium text-gray-500 truncate max-w-[80px]" data-id="element-448">
                          {levelDisplayLabel || 'Select...'}
                        </span>
                        <ChevronDownIcon size={14} className="text-gray-400" data-id="element-449" />
                      </div>
                    </button>
                  </div>

                  {/* Length Picker */}
                  <div className="flex-1 max-w-[200px]" ref={lengthDropdownRef} data-id="element-450">
                    <button onClick={() => setIsLengthDropdownOpen(!isLengthDropdownOpen)} className={`w-full flex items-center justify-between rounded-lg px-3 py-2 transition-colors border bg-white border-gray-200 hover:border-gray-300`} data-id="element-451">
                      <div className="flex items-center gap-2" data-id="element-452">
                        <ClockIcon size={14} className="text-gray-500" data-id="element-453" />
                        <span className="text-sm font-semibold text-gray-700" data-id="element-454">
                          Length
                        </span>
                      </div>
                      <div className="flex items-center gap-1" data-id="element-455">
                        <span className="text-sm font-medium text-gray-500" data-id="element-456">
                          {playbookLength === 'standard' ? 'Standard' : 'In-Depth'}
                        </span>
                        <ChevronDownIcon size={14} className="text-gray-400" data-id="element-457" />
                      </div>
                    </button>
                  </div>
                </div>}

              {/* Large Textarea */}
              <div className="flex-1 p-3 sm:p-6 overflow-y-auto sm:min-h-[200px] relative flex flex-col" data-id="element-458">
                <div className="flex-1 relative" data-id="element-459">
                  <textarea value={prompt} onChange={e => {
                setPrompt(e.target.value);
                const el = e.target;
                el.style.height = 'auto';
                el.style.height = el.scrollHeight + 'px';
              }} ref={el => {
                if (el) {
                  el.style.height = 'auto';
                  el.style.height = Math.max(200, el.scrollHeight) + 'px';
                }
                // @ts-ignore
                textareaRef.current = el;
              }} readOnly={isRecording} placeholder={isRecording ? 'Listening… your text will appear when done.' : 'Describe the assessment you want to create: topic, grade level, question types, difficulty…'} className={`w-full p-4 sm:p-6 bg-gray-50 border border-gray-200 rounded-xl text-sm sm:text-base leading-relaxed text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#00B75F] focus:border-[#00B75F] resize-none h-full sm:h-auto sm:min-h-[200px] ${isRecording ? 'opacity-50 cursor-default' : ''}`} autoFocus data-id="element-460" />
                  {isRecording && <div className="absolute inset-0 pointer-events-none p-4 sm:p-6" data-id="element-461">
                      <div className="w-full h-full flex flex-col justify-end" data-id="element-462">
                        <div className="w-full max-w-md mx-auto bg-white/90 backdrop-blur-sm border border-gray-200 rounded-xl p-4 shadow-sm mb-4" data-id="element-463">
                          <LiveWaveform analyserRef={analyserRef} data-id="element-464" />
                          <p className="text-sm font-medium text-gray-600 text-center mt-3 min-h-[20px]" data-id="element-465">
                            {liveTranscript || 'Listening...'}
                          </p>
                        </div>
                      </div>
                    </div>}
                </div>
              </div>

              {/* Footer */}
              <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between gap-2 sm:gap-3 flex-shrink-0" data-id="element-466">
                <div className="flex items-center" data-id="element-467">
                  <AnimatePresence mode="wait" data-id="element-468">
                    {!isRecording ? <motion.button key="dictate-btn" initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} exit={{
                  opacity: 0
                }} onClick={startRecording} className="flex items-center gap-1.5 rounded-lg transition-all duration-150 font-medium text-gray-500 hover:text-gray-700 bg-white hover:bg-gray-50 border border-gray-200 px-3 py-2 text-sm group" title="Dictate your instructions" data-id="element-469">
                        <MicIcon size={16} className="group-hover:scale-110 transition-transform" data-id="element-470" />
                        <span data-id="element-471">Dictate</span>
                      </motion.button> : <motion.div key="recording-toolbar" initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} exit={{
                  opacity: 0
                }} className="flex items-center gap-3" data-id="element-472">
                        <div className="relative flex items-center gap-1.5 bg-white border border-gray-200 px-3 py-2 rounded-lg" data-id="element-473">
                          <motion.div animate={{
                      opacity: [1, 0.3, 1]
                    }} transition={{
                      duration: 1.5,
                      repeat: Infinity
                    }} className="w-2 h-2 rounded-full bg-red-400" data-id="element-474" />
                          <span className="text-sm font-mono text-gray-500 tabular-nums" data-id="element-475">
                            {formatTime(recordingSeconds)}
                          </span>
                        </div>
                        <button onClick={cancelRecording} className="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors" title="Cancel (Esc)" data-id="element-476">
                          <XIcon size={16} data-id="element-477" />
                        </button>
                        <button onClick={stopRecording} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors" title="Stop & add (Enter)" data-id="element-478">
                          <SquareIcon size={12} data-id="element-479" />
                          <span data-id="element-480">Done</span>
                        </button>
                      </motion.div>}
                  </AnimatePresence>
                </div>
                <button onClick={() => setIsExpandedEditorOpen(false)} className="px-4 py-2 bg-gray-900 text-white text-sm font-semibold rounded-lg hover:bg-gray-800 transition-colors" data-id="element-481">
                  Done
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Sources Modal */}
      <AnimatePresence data-id="element-482">
        {isSourcesModalOpen && <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4" data-id="element-483">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setIsSourcesModalOpen(false)} data-id="element-484" />
            <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: 40
        }} transition={{
          duration: 0.25,
          ease: [0.4, 0, 0.2, 1]
        }} className="relative w-full sm:max-w-lg bg-white sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden max-h-[80vh] rounded-t-2xl" data-id="element-485">
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100" data-id="element-486">
                <h2 className="text-base font-bold text-gray-900" data-id="element-487">
                  Add Sources
                </h2>
                <button onClick={() => setIsSourcesModalOpen(false)} className="p-2 rounded-lg hover:bg-gray-50 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-488">
                  <XIcon size={18} data-id="element-489" />
                </button>
              </div>

              <div className="p-5 space-y-3 overflow-y-auto" data-id="element-490">
                {/* Upload */}
                <button onClick={() => fileInputRef.current?.click()} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all text-left" data-id="element-491">
                  <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0" data-id="element-492">
                    <UploadIcon size={16} className="text-blue-500" data-id="element-493" />
                  </div>
                  <div data-id="element-494">
                    <p className="text-sm font-semibold text-gray-900" data-id="element-495">
                      Upload Files
                    </p>
                    <p className="text-xs text-gray-400" data-id="element-496">
                      PDF, DOC, PPT, images (max {MAX_FILE_SIZE_MB}MB)
                    </p>
                  </div>
                </button>

                {/* Link */}
                <div className="rounded-xl border border-gray-200 overflow-hidden" data-id="element-497">
                  <button onClick={() => setShowLinkInput(!showLinkInput)} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-all text-left" data-id="element-498">
                    <div className="w-9 h-9 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0" data-id="element-499">
                      <LinkIcon size={16} className="text-purple-500" data-id="element-500" />
                    </div>
                    <div data-id="element-501">
                      <p className="text-sm font-semibold text-gray-900" data-id="element-502">
                        Paste a Link
                      </p>
                      <p className="text-xs text-gray-400" data-id="element-503">
                        Website, YouTube, or article URL
                      </p>
                    </div>
                  </button>
                  <AnimatePresence data-id="element-504">
                    {showLinkInput && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.2
                }} className="overflow-hidden" data-id="element-505">
                        <div className="px-4 pb-3 flex gap-2" data-id="element-506">
                          <input type="url" value={linkUrl} onChange={e => setLinkUrl(e.target.value)} placeholder="https://..." className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00B75F] focus:border-transparent" autoFocus onKeyDown={e => {
                      if (e.key === 'Enter') {
                        handleLinkAdd();
                        setIsSourcesModalOpen(false);
                        setShowLinkInput(false);
                      }
                    }} data-id="element-507" />
                          <button onClick={() => {
                      handleLinkAdd();
                      setIsSourcesModalOpen(false);
                      setShowLinkInput(false);
                    }} disabled={!linkUrl.trim()} className="px-3 py-2 bg-[#00B75F] text-white text-sm font-semibold rounded-lg hover:bg-[#00a055] disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-508">
                            Add
                          </button>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>

                {/* Web Search */}
                <button onClick={() => {
              setIsSourcesModalOpen(false);
              setIsSearchModalOpen(true);
            }} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all text-left" data-id="element-509">
                  <div className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0" data-id="element-510">
                    <GlobeIcon size={16} className="text-orange-500" data-id="element-511" />
                  </div>
                  <div data-id="element-512">
                    <p className="text-sm font-semibold text-gray-900" data-id="element-513">
                      Search the Web
                    </p>
                    <p className="text-xs text-gray-400" data-id="element-514">
                      Find and import web sources
                    </p>
                  </div>
                </button>

                {/* Paste Text */}
                <button onClick={() => {
              setIsSourcesModalOpen(false);
              setIsTextModalOpen(true);
            }} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all text-left" data-id="element-515">
                  <div className="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0" data-id="element-516">
                    <ClipboardListIcon size={16} className="text-green-500" data-id="element-517" />
                  </div>
                  <div data-id="element-518">
                    <p className="text-sm font-semibold text-gray-900" data-id="element-519">
                      Paste Text
                    </p>
                    <p className="text-xs text-gray-400" data-id="element-520">
                      Copy and paste content directly
                    </p>
                  </div>
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Text Modal */}
      <AnimatePresence data-id="element-521">
        {isTextModalOpen && <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4" data-id="element-522">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setIsTextModalOpen(false)} data-id="element-523" />
            <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: 40
        }} transition={{
          duration: 0.25,
          ease: [0.4, 0, 0.2, 1]
        }} className="relative w-full sm:max-w-lg bg-white sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden max-h-[80vh] rounded-t-2xl" data-id="element-524">
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100" data-id="element-525">
                <h2 className="text-base font-bold text-gray-900" data-id="element-526">
                  Paste Text
                </h2>
                <button onClick={() => setIsTextModalOpen(false)} className="p-2 rounded-lg hover:bg-gray-50 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-527">
                  <XIcon size={18} data-id="element-528" />
                </button>
              </div>
              <div className="p-5 flex flex-col gap-3" data-id="element-529">
                <textarea value={copiedText} onChange={e => setCopiedText(e.target.value)} placeholder="Paste your text content here..." className="w-full h-48 p-4 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#00B75F] focus:border-transparent resize-none" autoFocus data-id="element-530" />
                <div className="flex justify-end gap-2" data-id="element-531">
                  <button onClick={() => setIsTextModalOpen(false)} className="px-4 py-2 border border-gray-200 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-50 transition-colors" data-id="element-532">
                    Cancel
                  </button>
                  <button onClick={() => {
                handleTextAdd();
                setIsTextModalOpen(false);
              }} disabled={!copiedText.trim()} className="px-4 py-2 bg-[#00B75F] text-white text-sm font-semibold rounded-lg hover:bg-[#00a055] disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-533">
                    Add Text
                  </button>
                </div>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>;
}